import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    
    // Ensure proper initialization
    this.#initializeGallery();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Initialize gallery components
   */
  #initializeGallery() {
    // Ensure slideshow has proper touch capabilities
    if (this.slideshow && this.slideshow.refs?.scroller) {
      const scroller = this.slideshow.refs.scroller;
      // Ensure scroller has proper touch-action for horizontal scrolling
      if (scroller.style.touchAction !== 'pan-x') {
        scroller.style.touchAction = 'pan-x';
      }
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    
    // Try multiple selectors to find the media gallery
    let newMediaGallery = source.querySelector('media-gallery');
    
    // If not found at root, try looking deeper
    if (!newMediaGallery) {
      // The HTML might be a fragment containing the gallery
      // Try common wrapper elements
      const wrappers = source.querySelectorAll('[data-product-media], .product-media-gallery, [data-gallery]');
      for (const wrapper of wrappers) {
        newMediaGallery = wrapper.querySelector('media-gallery');
        if (newMediaGallery) break;
      }
    }
    
    // If still not found, check if source itself contains media-gallery as a child
    if (!newMediaGallery && source.innerHTML.includes('media-gallery')) {
      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = source.innerHTML;
      newMediaGallery = tempDiv.querySelector('media-gallery');
    }

    if (!newMediaGallery) {
      console.warn('MediaGallery: Could not find media-gallery element in variant update response');
      return;
    }

    // Store the current slideshow state before replacement
    const currentIndex = this.slideshow?.current || 0;
    
    // Clone the new gallery to ensure it's a proper DOM element
    const clonedGallery = newMediaGallery.cloneNode(true);
    
    // Add initialization marker
    clonedGallery.setAttribute('data-gallery-replaced', 'true');
    
    // Replace the gallery
    this.replaceWith(clonedGallery);
    
    // The cloned gallery should auto-initialize via its connectedCallback
    // But we'll also trigger a manual check
    requestAnimationFrame(() => {
      if (clonedGallery.slideshow) {
        // Force re-initialization if needed
        clonedGallery.slideshow.current = 0;
      }
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}